const en = {
  onboarding: {
    countryQuestion: "Got it! 🌍\n\nAnd which country are you in?",
    countryPlaceholder: "Which country are you in?",
    letsGo: "Let's go!",
    summaryIntro: "🎉 Great, {name}! You're all set!",
    yourNameLabel: "your name",
    baseLanguageLabel: "base language",
    goalsHint: "You can set your vocabulary goal and course duration anytime from the Goals page.",
    startLearning: "Start learning {destLang}!",
  },
  header: {
    learner: "Learner",
    home: "🏠 Home",
    allLessons: "📚 All Lessons",
    currentLesson: "📖 Current Lesson",
    flashcards: "🃏 Flashcards",
    progress: "📈 Progress",
    goals: "🎯 Goals",
    grammarLab: "📘 Grammar",
    more: "⋯ More",
    myVocabulary: "📝 My Vocabulary",
    listenSpeak: "🎧 Listen & Speak",
    signOut: "↩ Sign out",
  },
  dashboard: {
    greeting: "Hello, {name}! 👋",
    streakMsg: "{streak}-day streak 🔥 — keep it going!",
    noStreak: "Start your first lesson today!",
    streakProtectionDays: "{n} Streak Protection Days earned 🛡️",
    dayInfo: "Base language: {lang} · Day {day} of {total}",
    dayStreak: "day streak",
    share: "Share",
    wordsLearned: "Words Learned",
    lessonsDone: "Lessons Done",
    goalComplete: "Goal Complete",
    timeStudied: "Time Studied",
    hoursUnit: "Hours",
    paceTitle: "📊 Daily Pace Tracker",
    statusAhead: "🚀 {delta} words ahead!",
    statusBehind: "⚡ {delta} words to catch up",
    statusOnTrack: "✅ Right on track!",
    paceTarget: "Target: {wpd} words/day · Today: {today} words learned",
    paceExpected: "Expected: {n}",
    paceGoal: "Goal: {n}",
    actionCurrentLesson: "Current Lesson",
    actionAllLessons: "All Lessons",
    actionFlashcards: "Flashcards",
    actionProgress: "My Progress",
    actionGoals: "My Goals",
    actionMyVocabulary: "My Vocabulary",
    actionGrammarLab: "Grammar Lab",
    actionListenSpeak: "Listen & Speak Lab",
    lessonsSub: "{count} topics · {words} words",
    cardsDue: "{n} cards due today!",
    allCaughtUp: "All caught up! 🎉",
    progressSub: "Charts, accuracy, vocabulary growth",
    myVocabSub: "Browse every word you've learned",
    listenSpeakSub: "Practice speaking with Babi",
    goalsSub: "{words} words · {days} days",
    apiCost: "💰 API Cost Tracker",
    since: "since {date}",
    resetBtn: "↺ Reset",
    totalTokens: "total tokens",
    inputTokens: "input",
    outputTokens: "output",
    estCost: "est. cost",
    apiNote: "claude-sonnet-5 · $3/MTok in · $15/MTok out",
    tipLabel: "Tip:",
    tips: [
      "In Turkish, the verb always comes at the end of the sentence — \"Ben kahve içiyorum\" means \"I coffee am-drinking.\" Just absorb the patterns!",
      "Turkish uses vowel harmony — suffixes change their vowels to match the last vowel in the root (e.g. -lar/-ler, -da/-de). Once you spot it, everything clicks!",
      "Turkish has no grammatical gender. No masculine or feminine nouns — one less thing to memorise compared to French, Spanish or German!",
      "Personal pronouns (ben = I, sen = you) are usually dropped in Turkish because the verb suffix already tells you who is acting.",
      "Turkish is phonetically regular — every letter is always pronounced the same way. What you read is exactly what you say.",
      "Negation is formed by adding -me/-ma directly after the verb root: gel (come) → gelme (don't come), anla (understand) → anlama.",
      "Turkish uses postpositions that come AFTER the noun: \"İstanbul'da\" = \"in Istanbul\" (Istanbul + location suffix -da).",
      "The plural suffix follows vowel harmony: -lar after back vowels (a, ı, o, u) and -ler after front vowels (e, i, ö, ü). Akşam → akşamlar, gece → geceler.",
      "The suffix -ıyor/-iyor/-uyor/-üyor marks the present continuous tense. Anlıyorum = I am understanding (anla + ıyor + um).",
      "Turkish compound verbs combine a noun with etmek (to do) or olmak (to be): teşekkür etmek (to thank), hazır olmak (to be ready).",
    ],
  },
  lesson: {
    backBtn: "← Back",
    topicOf: "Topic {order} of {total} · {words} words",
    translating: "Preparing your lesson…",
    newWords: "✨ New Words ({n})",
    alreadyLearned: "🔄 Already Learned ({n})",
    practiceDialogue: "🎭 Practice",
    generateDialogue: "🎭 Give me a practice dialogue",
    listenSpeakPractice: "🎧 Practice in Listen & Speak Lab",
    generatingDialogue: "Babi is preparing your dialogue…",
    newDialogue: "↺ New dialogue",
    lessonSaved: "🎉 Lesson saved! Adding {n} new flashcards…",
    alreadyTaken: "✅ Lesson Already Taken",
    markComplete: "✅ Mark lesson complete & save flashcards",
    wordProgress: "Word {current} of {total}",
    nextWord: "Next Word",
    previousWord: "Previous",
  },
  vocab: {
    structure: "Structure",
    loadingExample: "Loading…",
    limitReached: "↺ Limit reached",
    anotherLeft: "↺ Another ({n} left)",
    anotherExample: "↺ Another example",
    analyzing: "Analyzing…",
    analyzed: "✓ Analyzed",
    analyzeWord: "🔍 Analyze Word",
    analysisIntro: "Here is the step-by-step grammatical analysis:",
    analysisError: "Could not load analysis. Please try again.",
    reportMistake: "🚩 Report mistake",
    reportWrongMeaning: "Wrong word meaning",
    reportWrongSentence: "Wrong sentence meaning",
    reportOther: "Other",
    reportChecking: "Checking…",
    reportThanks: "✓ Thanks for helping us improve!",
    reportNoIssue: [
      "I couldn't find any problem here — this looks correct to me!",
      "I don't see any issues here — this looks correct to me.",
      "Everything appears to be in order from my perspective.",
      "I couldn't identify any problems; this seems correct as written.",
      "This looks good to me — I don't have any concerns.",
      "I reviewed it and didn't find anything that needs changing.",
      "No issues stood out to me; the translation looks correct.",
      "From what I can tell, everything is correct.",
      "I wasn't able to spot any issues; this looks fine to me.",
    ],
    reportCheckError: "I had trouble completing the check. Please try again later.",
    reportOkBtn: "OK, I understand",
    reportEscalateBtn: "Escalate to a human reviewer",
    reportEscalatedMsg: "✓ Sent to our review team. Thank you!",
    reportAlreadyFlaggedMsg: "✓ This is already flagged for our review team — thank you!",
  },
  allLessons: {
    title: "📚 All Lessons",
    subtitle: "{count} topics · {words} words",
    searchPlaceholder: "🔍 Search topics or words…",
    noResults: "No topics match \"{search}\"",
    overwhelmedBanner: "You have {n} cards due for review. Please review your flashcards before starting new lessons.",
    overwhelmedLock: "Review flashcards first",
    overwhelmedBtn: "Go to Flashcards →",
  },
  flashcards: {
    title: "Flashcards",
    cardOf: "Card {current} of {total}",
    tapToReveal: "Tap card to reveal",
    hearIt: "🔊 Hear it",
    hearSentence: "🔊 Hear the sentence.",
    allConsolidated: "All Consolidated",
    noDue: "No cards are due right now.\nYour memory is well rested.",
    backToDashboard: "Back to Dashboard",
    sessionComplete: "Session Complete",
    memoryRetention: "Memory Efficiency",
    forgotten: "Forgotten",
    struggled: "Struggled",
    recalled: "Recalled",
    effortless: "Effortless",
    wordsConsolidated: "{retained} of {total} words memory consolidated",
    wordsInProgress: "{retained} of {total} words memory in progress",
    saveContinue: "Save & Continue",
    tomorrow: "Tomorrow",
    nDays: "{n} days",
    d1Title: "Comprehension",
    d1SubSkill: "To improve Reading and Listening skills",
    d1Journey: "New words begin their journey here.",
    d1Due: "{n} due",
    d1Listen: "Listen",
    d1ShowWord: "Show the Word",
    d2Title: "Production",
    d2SubSkill: "To improve Speaking and Writing skills",
    d2WillStart: "Will start later",
    d2Empty: "Words graduate here once you know them well in Comprehension flashcard set.",
    d2ModalTitle: "Production Practice unlocked!",
    d2ModalBody: "These are words you already recognise well. Now we are training you to produce them — this is how you go from understanding {targetLang} to actually speaking it.",
    d2ModalBtn: "Got it",
    leaveConfirmTitle: "Unsaved review progress",
    leaveConfirmBody: "You've reviewed cards in this session that haven't been saved yet. If you leave now without saving, that progress will be lost.",
    leaveConfirmSave: "Save and leave",
    leaveConfirmDiscard: "Discard and leave",
    leaveConfirmCancel: "Cancel",
    faq: {
      whyTwoQ: "Why Two Decks?",
      whyTwoA: "Learning a language has two separate skills that your brain stores differently: understanding words when you hear or read them, and producing them when you speak or write.\n\nMost flashcard apps only train one direction. Speafy trains both, in the right order.\n\nThe Comprehension flashcard set shows you a word in {targetLang} and asks you to recall its meaning. This builds the recognition skill your brain uses when listening or reading.\n\nThe Production flashcard set shows you a word in your {baseLang} and asks you to recall it in {targetLang}. This builds the retrieval skill your brain uses when speaking or writing. It is harder, and that difficulty is exactly what makes it effective.",
      whyLaterQ: "Why Does Production Flashcard Set Start Later?",
      whyLaterA: "Research in memory science shows that trying to produce a word before you recognise it reliably wastes effort and causes frustration. Speafy waits until your brain has genuinely consolidated a word through the Comprehension flashcard set, then automatically introduces it to the Production flashcard set so you can train production at exactly the right moment.",
      whyReturnQ: "Why Do Cards Keep Coming Back?",
      whyReturnA: "Every time you rate a card, Speafy uses a machine-learning algorithm called FSRS, based on years of memory research, to schedule your next review at the optimal moment: just before you would naturally forget it. Each successful recall stretches the interval further. Over time, words move from days to weeks to months between reviews, until they are permanently part of your long-term memory.",
    },
  },
  grammarLab: {
    title: "📘 Grammar Lab",
    subtitle: "{count} grammar points to explore",
    backBtn: "← Back",
    searchPlaceholder: "🔍 Search grammar points…",
    noResults: "No grammar points match \"{search}\"",
    allCategories: "All categories",
    allLevels: "All levels",
    notReviewedLabel: "Not reviewed",
    reviewedLabel: "Reviewed",
    dueBadge: "{n} due",
    explanationLabel: "Explanation",
    seeReference: "(see {term})",
    ruleLabel: "Rule",
    formsLabel: "Forms",
    translatingNote: "Translating…",
    practiceBtn: "▶ Practice this point",
    detailedExplanationBtn: "📖 Detailed explanation",
    practiceTitle: "Practice",
    practiceQuestion: "How do you say this in Turkish?",
    practiceOf: "{current} of {total}",
    practiceCorrectFeedback: "Correct!",
    practiceIncorrectFeedback: "Not quite — the answer was {answer}.",
    practiceNext: "Next →",
    practiceDone: "Practice complete",
    practiceScore: "{correct} of {total} correct",
    practiceNextReview: "Next review in {days} day{plural}",
    practiceReset: "Missed the mark this time — you'll see this point again tomorrow.",
    practiceAgainBtn: "Practice again",
    practiceCloseBtn: "Done",
  },
  placementTest: {
    title: "🎯 Placement Test",
    offerTitle: "Know some Turkish already?",
    offerBody: "Take a quick placement test — about 20 questions — to find your starting level. We'll set your Current Lesson to match, but every topic stays open to browse.",
    startBtn: "Start Test",
    notNowBtn: "Not Now",
    roundLabel: "Round {round} of {totalRounds}",
    questionLabel: "Question {n} of {total}",
    notSureOption: "I'm not sure",
    notSureHint: "(Select the fifth option if you're not 100% confident.)",
    wordPrompt: "What does this word mean?",
    listeningPrompt: "Listen and select the word you hear",
    listeningReplay: "Play Again",
    preparingRound: "Preparing your questions…",
    finishBtn: "See My Result",
    resultsTitle: "Your Placement Result",
    resultsLevel: "You're placed at {level}",
    resultsBody: "Your Current Lesson now starts at this level. You can still browse any earlier or later topic anytime from All Lessons.",
    continueBtn: "Continue",
  },
  goals: {
    title: "🎯 My Goals",
    displayName: "Display Name",
    namePlaceholder: "Your name…",
    vocabTarget: "Vocabulary Target",
    words: "words",
    minWords: "1,500 min",
    midWords: "2,000",
    maxWords: "3,500 max",
    courseDuration: "Target Course Duration",
    months: "Months",
    month: "month",
    monthsPlural: "months",
    daysSummary: "= {days} days",
    baseLanguage: "Base Language",
    otherLanguage: "Other languages (coming soon)",
    selectLanguage: "Select a language…",
    ok: "OK",
    langNames: {
      English: "English", Farsi: "Farsi (Persian)", Arabic: "Arabic",
      French: "French", German: "German", Spanish: "Spanish",
      Russian: "Russian", Portuguese: "Portuguese", Chinese: "Chinese",
      Turkish: "Turkish", Hindi: "Hindi", Italian: "Italian",
    },
    targetLanguage: "Target Language",
    targetLangNames: {
      Turkish: "Turkish",
    },
    wordsLbl: "words",
    wordsPerDay: "words/day",
    daysLbl: "days",
    saved: "✅ Goals saved!",
  },
  progress: {
    title: "📈 My Progress",
    memDistTitle: "Memory State Distribution · {n} cards",
    inAcquisition: "In Acquisition",
    rooted: "Rooted",
    fluent: "Fluent",
    deeplyRetained: "Deeply Retained",
    acqDesc: "< 3d stability",
    rootedDesc: "3 – 21d stability",
    fluentDesc: "21 – 90d stability",
    retainedDesc: "90d+ stability",
    forecastTitle: "Review Forecast — Next 15 Days",
    cardsDue: "Cards due",
    today: "Today",
    tmrw: "Tmrw",
    plusDays: "+{n}d",
    todayFull: "Today",
    tmrwFull: "Tomorrow",
    plusDaysFull: "+{n} days",
    goalProgress: "Overall Goal Progress",
    wordsLearned: "{learned} / {target} words learned",
    vocabGrowth: "Vocabulary Growth",
    noChart: "Complete your first lesson to see your chart! 📚",
    wordsLearnedSeries: "Words Learned",
    topicRings: "Topic Memory Rings",
    quickStats: "Quick Stats",
    dayStreak: "day streak 🔥",
    lessonsDone: "lessons done",
    totalStudy: "total study",
    avg14Day: "14-day average",
    minUnit: "min",
    dayLabel: "Day {n}",
    cards: "cards",
  },
  myVocab: {
    title: "📝 My Vocabulary",
    summaryTitle: "{n} Words Encountered",
    searchPlaceholder: "🔍 Search your words…",
    allStatuses: "All statuses",
    noResults: "No words match \"{search}\"",
    noWords: "Complete a lesson to start building your vocabulary! 📚",
    completedTopicsTitle: "Completed Topics ({n} of {total})",
    noDate: "—",
  },
  lab: {
    title: "Listen & Speak Lab",
    back: "Back",
    intro: "Have a short spoken conversation with Babi about a topic you've already studied.",
    pickTopic: "Pick a topic you've finished",
    noTopics: "Finish a lesson first, then come back here to practice speaking.",
    level: "Level {level}",
    talkAbout: "Talk to Babi about “{topic}”",
    startConversation: "Start Conversation",
    tapToStart: "Tap the mic and reply to Babi in Turkish.",
    speedLabel: "Babi's speed",
    speedNormal: "Normal",
    speedSlow: "Slow",
    speedNote: "Slow speed uses more of your voice minutes for the same reply.",
    continueConversation: "Answer the question",
    finishSession: "Finish",
    voiceReviewHint: "Check what you said, then confirm — or redo it.",
    voiceConfirm: "Confirm",
    voiceRedo: "Redo",
    voiceDenied: "Voice isn't available without microphone access. Tap the mic icon anytime to try again.",
    lowBalanceWarning: "You're running low on voice minutes for this month cycle.",
    thinking: "Babi is thinking…",
    complete: "Session complete",
    completeSub: "Nice practice. Come back any time to talk about another topic.",
    another: "Practice another topic",
    done: "Done",
    replyError: "Babi couldn't reply just now. Please try again in a moment.",
    tryAgain: "Try again",
    eliteUpsellTitle: "Listen & Speak is an Elite feature",
    eliteUpsellBody: "You're on the Pro plan. Upgrade to Elite in Settings to practice speaking with Babi.",
    listenAgainButton: "Listen again",
    listenAgainPlaying: "Stop",
    translateButton: "Translate",
    translating: "Translating…",
    translateFailed: "Couldn't translate — try again.",
    starsAriaLabel: "{stars} out of 3",
    explainButton: "Explain more",
    explaining: "Explaining…",
    explainFailed: "Couldn't get an explanation — try again.",
    recapHeading: "Conversation Report",
    recapTitle: "Your recap",
    recapAvgStars: "Average this session",
    recapTurnsCompleted: "{completed} of {total} turns completed",
    recapErrorFree: "{n} turns error-free",
    recapMistakesByType: "Mistakes by type",
    recapCorrections: "What got corrected",
    voiceMinutesTitle: "Voice minutes",
    voiceMinutesLeft: "{amount} of {total} left this cycle",
    voiceMinutesNone: "No monthly minutes left this cycle",
  },
  trial: {
    daysLeftBadge: "⏱ {n}d left in trial",
    expiredBadge: "Trial ended · Upgrade ↑",
    pausedBadge: "⏸ Paused",
    proBadge: "✦ Pro Plan",
    eliteBadge: "✦ Elite Plan",
  },
  paywall: {
    back: "← Back",
    title: "Upgrade to Speafy Pro",
    subTrialExpired: "Your free trial has ended. Upgrade to keep learning.",
    subDefault: "Unlock all lessons and flashcard review.",
    features: [
      "All {count} lessons fully unlocked",
      "Unlimited chat with Babi, your AI tutor",
      "Smart FSRS flashcard reviews",
      "Progress tracking & memory analytics",
      "Native audio on every word",
      "Practice dialogues",
      "Multi-language app interface",
    ],
    tierPro: "Pro",
    tierElite: "Elite",
    eliteFeatures: [
      "Everything in Speafy Pro",
      "Listen & Speak Lab — guided speaking practice with Babi",
      "3 hours of voice conversation with Babi every month",
      "Buy extra voice minutes anytime — $3/hour",
    ],
    billingMonthly: "Monthly",
    billingYearly: "Yearly",
    saveBadge: "Save 25%",
    pricePerMonth: "/month",
    priceBilledYearly: "billed yearly",
    cta: "Start Speafy Pro →",
    ctaElite: "Start Speafy Elite →",
    fine: "Cancel anytime · Secure checkout via Paddle",
    bannerExpired: "Your free trial has ended.",
    bannerActive: "Free trial — {n} day{s} remaining.",
    bannerBtn: "Upgrade ↑",
    settingsFreePlan: "Free Plan",
    pausedTitle: "Your subscription is paused",
    pausedSub: "Resume your subscription in Settings to unlock new lessons again.",
    pausedGoToSettingsBtn: "Go to Settings",
    alreadyProTitle: "You're already Pro!",
    alreadyProSub: "Your subscription is active — no need to purchase again.",
    alreadyProGoHomeBtn: "Go to Home",
    settingsTrialRemaining: "Trial — {n} day{s} remaining",
    settingsTrialEnded: "Trial ended — upgrade to unlock all lessons",
    settingsFreeForever: "Continue where you left off — upgrade to unlock all lessons",
    settingsUpgradeBtn: "Upgrade",
  },
  badges: {
    newbie: "Newbie",
    wordExplorer: "Word Explorer",
    wordNerd: "Word Nerd",
    wordWizard: "Word Wizard",
    rockstar: "Rockstar",
    expert: "Expert",
    champion: "Champion",
  },
  memStates: {
    inAcquisition: "In Acquisition",
    rooted: "Rooted",
    fluent: "Fluent",
    deeplyRetained: "Deeply Retained",
  },
  chat: {
    subtitle: "Your Turkish Tutor",
    greetWords: ["Hi", "Hello", "Dear"],
    greeting: "{greet} {name},\n\nAsk me anything about Turkish — grammar, vocabulary, culture — or about how to use this app.",
    placeholder: "Ask about Turkish or the app…",
    error: "Sorry, I couldn't connect right now. Please try again.",
    escalationFallback: "I'm sorry I couldn't fully help with that. I've flagged this for our team, and you'll receive an answer by email — typically within 2 business days. You can also reach us directly at support@speafy.com.",
    welcome: "Hello, {name}! 👋 Welcome to Speafy! I'm Babi, and I'm here for two things: helping you find your way around the app, and answering any questions you have about {targetLanguage}.\n\nStart with the current lesson, or pick a topic from All Lessons. Don't forget to mark each lesson as complete when you're done, then review your Flashcards afterward.\n\nJust ask me anytime — about the app or about {targetLanguage}.\n\nGood luck on your learning journey! 😊",
    lessonLimit: "You've already completed {limit} lessons today — that's a fantastic effort! 🌟\n\nTo keep your learning sustainable and prevent your flashcard queue from getting overwhelming, there's a daily limit of {limit} lessons.\n\nGive your brain some time to consolidate what you've learned today. How about a Flashcard review session instead? It's a great way to strengthen what you already know.\n\nI'll be here when you're ready for more tomorrow! 😊",
    streakDecayed: "Welcome back! 👋 You were away for {days} day(s). Thanks to the {protection} Streak Protection Day(s) 🛡️ you'd earned, your streak only dropped from {oldStreak} to {newStreak} instead of disappearing completely. Let's keep building!",
    streakWiped: "Welcome back! 👋 You were away for {days} day(s) — more than the {protection} Streak Protection Day(s) 🛡️ you had banked, so your streak restarted at 1. Every streak starts somewhere — let's build it back up!",
    weeklyRecapFallback: "Hey! 👋 I wanted to bring you your weekly recap, but I'm having trouble putting it together right now. Quick check-in though — you're on a {streak}-day streak, keep it going! 🔥",
    dailyPlanQuestion1: "Hey! 👋 How much time do you have to study today?",
    dailyPlanQuestion2: "Hi there! 😊 Got a study plan for you — how many minutes can you spare today?",
    dailyPlanQuestion3: "Ready for today's plan? 🎯 Just tell me roughly how much time you've got.",
    dailyPlanClarify: "Sorry, I didn't quite catch that! Could you give me a rough number, like '20' or '30 minutes'?",
    dailyPlanUnreasonable: "That doesn't quite sound like a typical study session! Could you give me something between 5 minutes and about 5 hours?",
    dailyPlanGiveUp: "No worries — whenever you're ready, just head to Flashcards or your Current Lesson and dive in! 😊",
    dailyPlanFallback: "I've got your available time noted, but I'm having trouble building today's plan right now. In the meantime, Flashcards is always a safe bet if you've got due cards waiting! 😊",
    sendBtn: "Send",
    askBabi: "Ask Babi",
    voiceConsentTitle: "Turn on voice chat with Babi?",
    voiceConsentBody: "Babi will use your microphone to hear you and your speaker to reply. Your speech is converted to text — the audio recording itself is never kept. The text may be kept for up to 30 days. See our Privacy Policy for details.",
    voiceConsentPrivacyLink: "Privacy Policy",
    voiceConsentAllow: "Allow microphone",
    voiceConsentDecline: "Not now",
    voiceDenied: "Voice isn't available without microphone access. You can keep chatting by typing — tap the mic icon anytime to try again.",
    voiceDeviceInUse: "Your microphone seems to be in use elsewhere — close other apps using it and try again.",
    voiceError: "Something went wrong with voice chat. Please try again.",
    voiceStarting: "Starting… please wait",
    voiceListening: "Listening…",
    voiceProcessing: "Understanding…",
    voiceReviewHint: "Tap to edit, or this will send automatically in a few seconds.",
    voiceSendNow: "Send now",
    voiceDiscard: "Discard",
    voiceEditTranscript: "Edit what Babi heard",
    voiceBadgeLabel: "Sent by voice",
    voiceDidntCatch: "Didn't catch that — tap the mic to try again.",
    voiceSpeaking: "Babi is speaking…",
    voiceTtsUnavailable: "Audio wasn't available for that reply — showing the text instead.",
    voiceTtsUnavailableLanguage: "Babi's spoken reply isn't available in {language} yet.",
    voiceTapToHear: "Tap to hear Babi's reply",
    voiceNotEntitled: "Voice chat with Babi is a premium feature — upgrade to use it.",
    voiceNoBalance: "You've used all your voice minutes for this cycle. They'll refresh next cycle.",
    voiceOtherTabActive: "Voice chat is already active in another tab.",
    voiceLowBalanceNudge: "Running low on voice minutes this cycle.",
    voiceLowMinutesHeadsUp: "You're running low on voice minutes this cycle.",
    voiceGetMoreMinutes: "Get more minutes",
    voiceStopBtn: "Stop",
    voiceMicButtonLabel: "Voice chat",
    voiceBannerDismiss: "Dismiss",
  },
  settings: {
    title: "Settings",
    accountSection: "Account",
    linkedGoogle: "Linked with Google",
    emailAccountBadge: "Email account",
    changeNameBtn: "Change user name",
    nameSaveBtn: "Save",
    nameCancelBtn: "Cancel",
    nameSaving: "Saving…",
    passwordLabel: "Password",
    passwordSub: "Send a reset link to your email",
    passwordSent: "Sent ✓",
    passwordResetBtn: "Reset",
    subscriptionSection: "Subscription",
    subscriptionAdminLabel: "Admin Account",
    subscriptionAdminSub: "You have full, permanent access to all features.",
    subscriptionReviewerLabel: "Reviewer Access",
    subscriptionReviewerSub: "You have free access to all features as an app reviewer.",
    subscriptionProPlan: "Speafy Pro — {plan}",
    subscriptionElitePlan: "Speafy Elite — {plan}",
    subscriptionPlanAnnual: "Annual",
    subscriptionPlanMonthly: "Monthly",
    subscriptionRenews: "Renews {date}",
    subscriptionActive: "Active",
    subscriptionManageLink: "Billing & Invoices",
    subscriptionCancelLink: "Cancel or pause subscription",
    subscriptionPaymentFailedLabel: "Your last payment failed",
    subscriptionPaymentFailedSub: "Payment failed on {date}",
    subscriptionPaymentFailedSubNoDate: "Please update your payment method to keep your Pro access",
    subscriptionUpdatePaymentLink: "Update payment method ↗",
    subscriptionCancelledPending: "Your plan ends on {date} — you'll keep Pro access until then",
    subscriptionPausedLabel: "Subscription paused",
    subscriptionPausedSub: "Resumes automatically on {date} — you'll be billed in full for a new period unless you resume early",
    subscriptionPausedSubNoDate: "Resumes automatically after 3 months unless you resume early",
    subscriptionResumeBtn: "Resume now",
    subscriptionResuming: "Resuming…",
    pausePendingLabel: "Pause scheduled",
    pausePendingSub: "Takes effect on {date}, at the end of your current billing period",
    manageOfferTitle: "Thinking about canceling?",
    manageOfferSub: "Pause instead — you'll keep your progress through {date}, then automatically resume and be billed in full for a new period after 3 months, unless you resume sooner.",
    manageOfferPauseBtn: "Pause instead",
    manageOfferContinueLink: "Cancel anyway",
    manageOfferDismiss: "Never mind",
    pauseOfferLabel: "Need a break?",
    pauseOfferSub: "Pause your subscription for 3 months instead of canceling — keep your progress, resume anytime.",
    pauseOfferBtn: "Pause subscription",
    pauseConfirmP1: "Pause your Speafy Pro subscription?",
    pauseConfirmC1: "Your current billing period continues until {date} — you keep everything you've already paid for.",
    pauseConfirmC2: "While paused, new Lessons are locked (same as an expired free plan), but you can still review your existing Flashcards normally.",
    pauseConfirmC3: "The pause lasts a fixed 3 months. If you don't resume manually before then, you'll automatically resume and be billed in full for a new period.",
    pauseConfirmBtn: "Confirm pause",
    pausing: "Pausing…",
    pauseFailedGeneric: "Something went wrong pausing your subscription. Please try again or contact support.",
    manageLinkLoading: "Loading…",
    manageLinkFailedGeneric: "Couldn't load a management link right now. Please try again.",
    resumeConfirmP1: "Resume your Speafy Pro subscription now?",
    resumeConfirmC1: "You'll be billed in full for a new billing period right away.",
    resumeConfirmBtn: "Yes, resume now",
    resumeFailedGeneric: "Something went wrong resuming your subscription. Please try again or contact support.",
    subscriptionLastCharge: "Last charge: {amount} on {date} — {status}",
    subscriptionLastChargePaid: "Paid",
    subscriptionLastChargeFailed: "Failed",
    tierChangePendingLabel: "Plan change scheduled",
    tierChangePendingToElite: "Switching to Elite on {date}",
    tierChangePendingToBasic: "Switching to Pro on {date}, at the end of your current billing period",
    tierUpgradeToEliteLink: "Upgrade to Elite",
    tierDowngradeToBasicLink: "Switch back to Pro",
    tierUpgradeConfirmTitle: "Upgrade to Elite?",
    tierUpgradeConfirmSub: "You'll be charged the prorated difference today, and get Elite's 3 hours of voice conversation with Babi right away — {price}/{interval} going forward.",
    tierDowngradeConfirmTitle: "Switch back to Pro?",
    tierDowngradeConfirmSub: "You'll keep Elite through {date}, then switch to Pro at your next billing date — no charge today.",
    tierUpgradeConfirmBtn: "Yes, upgrade now",
    tierDowngradeConfirmBtn: "Yes, switch to Pro",
    tierChangeBusy: "Updating…",
    tierChangeFailedGeneric: "Something went wrong changing your plan. Please try again or contact support.",
    voiceMinutesLabel: "Voice minutes ({tier})",
    voiceTierElite: "Elite",
    voiceTierBasic: "Pro",
    voiceTierTrial: "Trial",
    voiceTierAdmin: "Admin",
    voiceTierViewer: "Viewer",
    voiceMinutesUsed: "{used} used of {limit} this cycle",
    voiceMinutesPurchasedExtra: "Plus {extra} purchased — used after your monthly minutes run out, and never expire",
    yourDataSection: "Your Data",
    marketingLabel: "Marketing Communications Consent",
    marketingSub: "Updates, notifications, and special offers",
    voiceConsentLabel: "Voice Chat with Babi",
    voiceConsentSub: "Allow Babi to hear you and reply out loud",
    privacyLegalSection: "Privacy, Terms & Legal",
    privacyPolicyLabel: "Privacy Policy",
    privacyPolicySub: "How we collect, use, and protect your data",
    viewLink: "View",
    termsLabel: "Terms of Service",
    termsSub: "Rules and conditions governing your use",
    refundLabel: "Refund Policy",
    refundSub: "Eligibility and how to request a refund",
    cookieSettingsLabel: "Cookie Settings",
    cookieSettingsSub: "Manage analytics and functional cookies",
    manageLink: "Manage",
    privacySection: "Privacy & Data",
    exportLabel: "Export my data",
    exportSub: "Download a JSON file of your profile, progress, and flashcards",
    exportBtn: "Export",
    exportDone: "✓ Downloaded",
    cacheLabel: "Local cache",
    cacheSub: "Learning progress and example sentences are cached in your browser for speed.",
    cookieLabel: "Cookie & privacy settings",
    cookieSub: "Analytics and cookie preferences are managed on the Speafy website",
    privacyLink: "Privacy policy ↗",
    aboutSection: "About",
    aiTutorLabel: "AI tutor",
    aiTutorSub: "Powered by Claude Sonnet — Anthropic",
    algoLabel: "Memory algorithm",
    algoSub: "FSRS v4.5 — Free Spaced Repetition Scheduler",
    versionLabel: "Version",
    learningSection: "Learning",
    placementTestLabel: "Placement test",
    placementTestSubNew: "Find your starting level with a quick test",
    placementTestSubRetake: "Last placed at {level} · retake anytime",
    placementTestBtnNew: "Take Test",
    placementTestBtnRetake: "Retake Test",
    appearanceSection: "Appearance",
    darkModeLabel: "Dark mode",
    darkModeSub: "Switch to a dark colour scheme",
    largeTextLabel: "Large text",
    largeTextSub: "Increases text size for easier reading",
    babiCheckinsSection: "Babi Check-ins",
    babiDailyNudgeLabel: "Babi's daily nudge",
    babiDailyNudgeSub: "Babi asks how much time you have and suggests a study plan each day",
    babiWeeklyReportLabel: "Babi's weekly report",
    babiWeeklyReportSub: "A weekly recap of your progress, once a week",
    dangerSection: "Danger Zone",
    resetLabel: "Reset all progress",
    resetSub: "Wipes your streak, flashcards, and lesson history. Cannot be undone.",
    resetBtn: "Reset",
    resetConfirmP1: "Reset all progress? This permanently deletes:",
    resetConfirmC1: "All {n} flashcards and their memory data",
    resetConfirmC2: "Your streak ({n} days) and study history",
    resetConfirmC3: "All lesson completion records, across every language you're learning",
    resetConfirmC4: "Your Grammar Lab review progress",
    resetConfirmP2: "Your account, name, and language settings will be kept.",
    resetConfirmBtn: "Yes, reset everything",
    resetting: "Resetting…",
    resetDone: "✅ Progress reset! You're starting fresh.",
    deleteLabel: "Delete account",
    deleteSub: "Permanently deletes your account and all data. Cannot be undone.",
    deleteBtn: "Delete account",
    deleteConfirmP1: "Permanently delete your Speafy account? This will immediately and irreversibly delete:",
    deleteConfirmC1: "Your profile (name, language, goals)",
    deleteConfirmC2: "All {n} flashcards and memory data",
    deleteConfirmC3: "Your full learning history and streak",
    deleteConfirmC4: "Your Firebase authentication account",
    deleteConfirmPrompt: "Type DELETE (all caps) to confirm:",
    deleteConfirmBtn: "Permanently delete my account",
    deleting: "Deleting…",
    cancelBtn: "Cancel",
  },
  search: {
    trigger: "Search",
    title: "🔍 Search",
    close: "✕ Close",
    clear: "Clear search",
    placeholder: "Search words, topics, grammar…",
    emptyPrompt: "Start typing to search words, topics, and grammar points.",
    noResults: "No results for \"{search}\"",
    resultsLabel: "Search results",
    topicsLabel: "Topics",
    wordsLabel: "Words",
    grammarLabel: "Grammar",
    badgeTopic: "Topic",
    badgeWord: "Word",
    badgeGrammar: "Grammar",
    proTag: "Pro",
  },
  authAction: {
    verifyingTitle: "Verifying your email…",
    verifiedTitle: "Your email is verified!",
    verifiedBody: "You're all set. Continue to Speafy to keep learning.",
    continueBtn: "Continue to Speafy →",
    verifyErrorTitle: "This link has expired",
    verifyErrorBody: "This verification link is no longer valid. Sign in and we'll send you a new one.",
    resendBtn: "Resend verification email",
    resendSent: "Sent ✓",
    resendError: "Could not resend. Please try again in a moment.",
    signInBtn: "Sign in",
    verifyingReset: "Checking your link…",
    resetTitle: "Reset your password",
    resetBodyFor: "Choose a new password for {email}",
    newPasswordPh: "New password",
    confirmPasswordPh: "Confirm new password",
    resetSubmitBtn: "Reset password",
    resettingBtn: "Resetting…",
    resetSuccessTitle: "Password updated!",
    resetSuccessBody: "Your password has been changed. Sign in with your new password.",
    resetErrorTitle: "This link has expired",
    resetErrorBody: "This password reset link is no longer valid. Request a new one from the sign-in screen.",
    passwordMismatch: "Passwords don't match.",
    passwordTooShort: "Password must be at least 6 characters.",
    genericError: "Something went wrong. Please try again.",
    invalidTitle: "This link is invalid or has expired",
    invalidBody: "The link you followed is no longer valid. Please return to Speafy and try again.",
    backToSignIn: "Back to sign in",
  },

  // ── Teacher-partner affiliate program ──────────────────────────────────────
  // FINALIZED wording — provided verbatim by the team. Do NOT edit or re-translate.
  // `reviewed` is a native-speaker sign-off flag: a fluent speaker flips it to `true`
  // once they've confirmed this bundle's sentence reads well. Only en/es/fr/de/fa carry
  // this string; every other language falls back to English here by design.
  // Consumed from a single source (these bundles) by two surfaces:
  //   • the in-app teacher dashboard — via t("affiliate.disclosure.text")
  //   • the teacher pitch page — via scripts/build-affiliate-strings.js, which extracts
  //     this block into functions/affiliate/affiliate-strings.json (served by affiliateGate)
  affiliate: {
    disclosure: {
      text: "Just so you know: if you sign up for Speafy through my link, I earn a small commission — it doesn't cost you anything extra.",
      reviewed: false,
    },
    // Promo message a teacher copies to recruit their students. FINALIZED + native-verified
    // (reviewed: true). `{{referral_link}}` is a LITERAL placeholder — the dashboard / pitch
    // page substitutes the real link at send time; i18n t()'s single-brace {x} interpolation
    // never touches double-brace tokens.
    promoTemplate: {
      text: "Hey everyone! I want to recommend an app that's a great add-on to our classes: Speafy. It follows the exact topics we study together, and you can practice speaking anytime with an AI conversation partner named Babi. It's free to start, and I'll get a weekly report on your progress — so I can see how you're doing between our sessions. Sign up through this link so it's linked to me as your teacher: {{referral_link}}",
      reviewed: true,
    },
  },

  // Teacher-partner role only — the Teacher Dashboard link (Header + Home) and the
  // Teacher Dashboard screen itself (/teacher-dashboard). `link` is used in both link places.
  // Shown purely on profile.role, never on subscription tier.
  teacherDash: {
    link: "Teacher Dashboard",
    title: "Teacher Dashboard",
    backHome: "Back to Home",
    // Student-facing (not teacher-facing): the "My Teacher" badge on the Dashboard for a
    // referred student. {name} is a denormalized copy of the teacher's name (see linkStudentToTeacher).
    studentBadge: "Your teacher: {name}",
    // ── Teacher Dashboard page ──
    intro: "Everything you need to bring your students onto Speafy and follow their progress.",
    referralTitle: "Your referral link",
    referralBody: "Share this link with your students. Anyone who signs up through it is linked to you as their teacher.",
    copy: "Copy link",
    copied: "Copied",
    // ── Quick-share buttons under the referral link ──
    shareText: "Join me on Speafy and learn a language with me! {link}",
    shareTelegramText: "Join me on Speafy and learn a language with me!",
    shareEmailSubject: "Join me on Speafy",
    shareWhatsapp: "WhatsApp",
    shareTelegram: "Telegram",
    shareEmail: "Email",
    shareSms: "SMS",
    shareMore: "More",
    studentsTitle: "Your students",
    studentsLoading: "Loading your students…",
    studentsError: "We couldn't load your students just now.",
    retry: "Try again",
    studentsEmpty: "No students yet. Share your referral link — students who join through it will show up here.",
    colName: "Student",
    colJoined: "Joined",
    colStatus: "Subscription",
    colCommission: "Commission earned",
    colGrammar: "Grammar focus",
    statusActive: "Active",
    statusTrial: "Trial",
    statusNone: "Not subscribed",
    commissionNote: "This is what you've earned from each student overall. Your available balance for payout is shown below.",
    commissionCapReached: "12-month period ended",
    grammarNote: "Grammar mistake data is shown only for students with an active subscription.",
    studentUnnamed: "Student",
    curriculumTitle: "Curriculum reference",
    curriculumBody: "Browse every topic and word your students learn, in order.",
    curriculumLink: "Open curriculum reference",
    profileTitle: "Your teacher profile",
    profileBody: "This is how you'll appear in the Speafy teacher directory once it launches.",
    profileBio: "Short bio",
    profileBioPh: "A sentence or two about you and how you teach.",
    profilePhoto: "Photo",
    photoChoose: "Choose photo",
    photoUploading: "Uploading…",
    photoRemove: "Remove",
    photoHint: "PNG or JPG, up to 200 KB.",
    photoTypeErr: "Please choose a PNG or JPG image.",
    photoSizeErr: "That image is over 200 KB — please choose a smaller one.",
    photoUploadErr: "Upload failed. Please try again.",
    profileLink: "Personal Website or Social Media link",
    profileLinkPh: "www.example.com",
    profileLanguages: "Languages I teach",
    profileLanguagesHint: "Pick the languages you teach. More open up as Speafy adds them as course languages.",
    profileLanguagesSoon: "Coming soon",
    save: "Save",
    saving: "Saving…",
    saved: "Saved",
    urlError: "That doesn't look like a web address. Check it, or leave the field blank.",
    // Partner Terms status line (shown near the top of the Teacher Dashboard)
    termsStatusAccepted: "Teacher Partner Terms — Accepted {date}",
    termsStatusPending: "Teacher Partner Terms — Not yet accepted",
    termsVersionLabel: "Version {version}",
    termsView: "View accepted Terms",
    // ── Payout request (affiliate roadmap step 23) ──
    payoutTitle: "Payout",
    payoutLoading: "Loading your payout status…",
    payoutError: "We couldn't load your payout status just now.",
    payoutBalance: "Your current balance: {balance}",
    payoutNotEligible: "You'll be able to request a payout once your balance reaches {threshold}.",
    payoutAlreadyRequested: "Payout requested — we'll be in touch.",
    requestPayoutBtn: "Request Payout",
    payoutRequestFailed: "Something went wrong submitting your request. Please try again.",
    payoutConfirmTitle: "Request a payout?",
    payoutConfirmBody: "You currently have {balance} available to request.\n\nThis starts a manual payout process — no money moves automatically. We'll review your request and follow up by email.",
    payoutConfirmBtn: "Request Payout",
    payoutConfirmCancel: "Cancel",
  },

  // ── Teacher Partner Terms: in-app acceptance screen + Home banner ──
  // The Terms body itself is English only (src/teacherTermsContent.js); only this chrome and the
  // checkbox/button labels vary — and the checkbox + button labels stay English as contract text.
  teacherTerms: {
    screenTitle: "Teacher Partner Terms",
    contents: "Contents",
    lastUpdatedLabel: "Last updated",
    effectiveLabel: "Effective date",
    bannerTitle: "Action needed",
    bannerBody: "Please review and accept the Teacher Partner Terms to continue as a partner.",
    bannerCta: "Review & accept",
    acceptedOn: "You accepted these Terms on {date}.",
    acceptedVersion: "Version accepted: {version}",
    accepting: "Recording your acceptance…",
    acceptError: "We couldn't record your acceptance just now. Please try again.",
    draftNotice: "These Terms are not final yet. You'll be able to accept them once the effective date is set.",
  },
};

export default en;
